import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '../services/api';

const s = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7fa', padding: 24 },
  card: { background: '#fff', borderRadius: 12, padding: 40, width: 480, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
  sub: { color: '#666', marginBottom: 24, fontSize: 14 },
  sectionTitle: { fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, marginTop: 20, borderBottom: '1px solid #f0f0f0', paddingBottom: 6 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 5 },
  input: (hasError) => ({
    width: '100%', padding: '10px 14px', fontSize: 14, marginBottom: 4,
    border: `1px solid ${hasError ? '#ef4444' : '#ddd'}`, borderRadius: 6,
    boxSizing: 'border-box',
  }),
  select: { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, marginBottom: 4, background: '#fff', boxSizing: 'border-box' },
  errorText: { color: '#ef4444', fontSize: 12, marginBottom: 10, display: 'block' },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#dc2626', fontSize: 13 },
  successBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#166534', fontSize: 13 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  btn: { width: '100%', padding: '12px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 20, marginBottom: 16 },
  link: { color: '#7c83fd', textDecoration: 'none', fontWeight: 600 },
  footer: { textAlign: 'center', fontSize: 13, color: '#666' },
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [errors, setErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [form, setForm] = useState({
    tenantName: '', plan: 'BASIC',
    firstName: '', lastName: '',
    email: '', password: '', confirmPassword: '',
  });

  const set = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
    if (serverError) setServerError('');
  };

  const handleLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const e = {};
    if (!form.tenantName.trim()) e.tenantName = 'Company name is required';
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!form.email) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 8) e.password = 'Password must be at least 8 characters';
    if (!form.confirmPassword) e.confirmPassword = 'Please confirm your password';
    else if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }
    setErrors({});
    setLoading(true);
    try {
      // multipart/form-data — "data" part is JSON, "logo" part is the image file
      const formData = new FormData();
      formData.append('data', new Blob([JSON.stringify({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        tenantName: form.tenantName,
        tenantPlan: form.plan,
        role: 'TENANT_ADMIN',
      })], { type: 'application/json' }));
      if (logoFile) formData.append('logo', logoFile);

      await authApi.registerTenant(formData);
      navigate('/login', { state: { message: 'Account created! Please sign in.' } });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Registration failed. Please try again.';
      setServerError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔐</div>
        <h1 style={s.title}>Create Account</h1>
        <p style={s.sub}>Set up your organisation on SecureOffer</p>

        {serverError && <div style={s.errorBox}>⚠️ {serverError}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div style={s.sectionTitle}>Organisation</div>

          <label style={s.label}>Company Name</label>
          <input style={s.input(!!errors.tenantName)} placeholder="Acme Corp"
            value={form.tenantName} onChange={set('tenantName')} />
          {errors.tenantName && <span style={s.errorText}>{errors.tenantName}</span>}

          <label style={s.label}>Company Logo <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span></label>
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
              border: '1px dashed #ddd', borderRadius: 8, padding: '10px 14px',
              background: '#fafafa',
            }}>
              {logoPreview
                ? <img src={logoPreview} alt="logo preview" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 6 }} />
                : <span style={{ fontSize: 28 }}>🏢</span>
              }
              <span style={{ fontSize: 13, color: '#666' }}>
                {logoFile ? logoFile.name : 'Click to upload PNG, JPG or SVG'}
              </span>
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogo} />
            </label>
          </div>

          <label style={s.label}>Plan</label>
          <select style={s.select} value={form.plan} onChange={set('plan')}>
            <option value="BASIC">Basic</option>
            <option value="PRO">Pro</option>
            <option value="ENTERPRISE">Enterprise</option>
          </select>

          <div style={s.sectionTitle}>Admin User</div>

          <div style={s.row}>
            <div>
              <label style={s.label}>First Name</label>
              <input style={s.input(!!errors.firstName)} placeholder="Jane"
                value={form.firstName} onChange={set('firstName')} />
              {errors.firstName && <span style={s.errorText}>{errors.firstName}</span>}
            </div>
            <div>
              <label style={s.label}>Last Name</label>
              <input style={s.input(!!errors.lastName)} placeholder="Doe"
                value={form.lastName} onChange={set('lastName')} />
              {errors.lastName && <span style={s.errorText}>{errors.lastName}</span>}
            </div>
          </div>

          <label style={s.label}>Work Email</label>
          <input style={s.input(!!errors.email)} type="email" placeholder="jane@acme.com"
            value={form.email} onChange={set('email')} />
          {errors.email && <span style={s.errorText}>{errors.email}</span>}

          <label style={s.label}>Password</label>
          <input style={s.input(!!errors.password)} type="password" placeholder="Min 8 characters"
            value={form.password} onChange={set('password')} />
          {errors.password && <span style={s.errorText}>{errors.password}</span>}

          <label style={s.label}>Confirm Password</label>
          <input style={s.input(!!errors.confirmPassword)} type="password" placeholder="Repeat password"
            value={form.confirmPassword} onChange={set('confirmPassword')} />
          {errors.confirmPassword && <span style={s.errorText}>{errors.confirmPassword}</span>}

          <button style={{ ...s.btn, ...(loading ? { opacity: 0.7, cursor: 'not-allowed' } : {}) }}
            type="submit" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div style={s.footer}>
          Already have an account? <Link to="/login" style={s.link}>Sign In</Link>
        </div>
      </div>
    </div>
  );
}
